
import { useState } from "react";
import { Menu } from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";

interface NavigationProps {
  onLogin: () => void;
  onSignup: () => void;
}

const Navigation = ({
  onLogin,
  onSignup
}: NavigationProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Function to handle navigation
  const handleNavigation = (route: string) => {
    if (location.pathname === '/') {
      // If on landing page, scroll to the section
      const element = document.getElementById(route);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        setIsMenuOpen(false);
      }
    } else {
      // If on another page, navigate to landing page with hash
      navigate(`/#${route}`);
      // Ensure we scroll to the section after navigation
      setTimeout(() => {
        const element = document.getElementById(route);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
      setIsMenuOpen(false);
    }
  };
  
  // Handle Contact navigation
  const handleContactNavigation = () => {
    if (location.pathname === '/contact') {
      // If already on contact page, scroll to the top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // If on another page, navigate to contact page
      navigate('/contact');
      // Ensure we scroll to top when page loads
      window.scrollTo(0, 0);
    }
    setIsMenuOpen(false);
  };
  
  // Handle logo click to return to home
  const handleLogoClick = () => {
    navigate('/');
  };
  
  return <nav className="fixed top-4 left-1/2 -translate-x-1/2 w-[95%] max-w-6xl z-50">
     <div className="glass-panel h-16 relative rounded-full px-4 flex items-center justify-between shadow-lg backdrop-blur-md overflow-visible">
        <div className="relative h-full cursor-pointer flex items-center" onClick={handleLogoClick}>
  <img 
    src="/lovable-uploads/b9e6fa8f-f749-4515-b67c-d28715d804c5.png" 
    alt="The Mat Monster" 
    className="h-24 w-auto -my-4"
  />
</div>
        
        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          <a 
            onClick={() => handleNavigation('features')} 
            className="text-neutral-600 hover:text-primary transition-colors font-medium cursor-pointer"
          >
            Features
          </a>
          <a 
            onClick={() => handleNavigation('pricing-section')} 
            className="text-neutral-600 hover:text-primary transition-colors font-medium cursor-pointer"
          >
            Pricing
          </a>
          <a 
            onClick={() => handleNavigation('faq-section')} 
            className="text-neutral-600 hover:text-primary transition-colors font-medium cursor-pointer"
          >
            FAQs
          </a>
          <a 
            onClick={handleContactNavigation} 
            className="text-neutral-600 hover:text-primary transition-colors font-medium cursor-pointer"
          >
            Contact
          </a>
        </div>
        
        <div className="hidden md:flex items-center gap-4">
  <button 
    className="px-4 py-2 text-primary hover:text-primary/80 transition-colors font-medium" 
    onClick={onLogin}
  >
    Log in
  </button>
  
  <div className="mr-4">
    <button className="bg-primary text-white rounded-lg px-6 py-3 font-medium hover:bg-primary/90 transition-colors" onClick={onSignup}>
      Join Free
    </button>
  </div>
</div>

        {/* Mobile Menu Button */}
        <button className="md:hidden p-2 hover:bg-neutral-200/50 rounded-full transition-colors" onClick={() => setIsMenuOpen(!isMenuOpen)}>
          <Menu className="w-6 h-6" />
        </button>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && <motion.div className="md:hidden glass-panel mt-1 rounded-xl p-3 shadow-lg" initial={{
      opacity: 0,
      y: -20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.2
    }}>
          <div className="flex flex-col gap-4">
            <a 
              onClick={() => handleNavigation('features')} 
              className="text-neutral-600 hover:text-primary transition-colors font-medium px-4 py-2 hover:bg-neutral-200/50 rounded-lg cursor-pointer"
            >
              Features
            </a>
            <a 
              onClick={() => handleNavigation('pricing-section')} 
              className="text-neutral-600 hover:text-primary transition-colors font-medium px-4 py-2 hover:bg-neutral-200/50 rounded-lg cursor-pointer"
            >
              Pricing
            </a>
            <a 
              onClick={() => handleNavigation('faq-section')} 
              className="text-neutral-600 hover:text-primary transition-colors font-medium px-4 py-2 hover:bg-neutral-200/50 rounded-lg cursor-pointer"
            >
              FAQs
            </a>
            <a 
              onClick={handleContactNavigation} 
              className="text-neutral-600 hover:text-primary transition-colors font-medium px-4 py-2 hover:bg-neutral-200/50 rounded-lg cursor-pointer"
            >
              Contact
            </a>
            <hr className="border-neutral-200" />
            <button className="text-primary hover:text-primary/80 transition-colors font-medium px-4 py-2 hover:bg-neutral-200/50 rounded-lg text-center" onClick={onLogin}>
              Log in
            </button>
            <button className="bg-primary text-white rounded-lg px-6 py-3 font-medium hover:bg-primary/90 transition-colors w-full" onClick={onSignup}>
              Join Free
            </button>
          </div>
        </motion.div>}
    </nav>;
};

export default Navigation;
